import { memo, useMemo } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { MapPin, Globe, Building2, ArrowRight } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { getStats } from '@/data/locations'

interface StatItem {
  icon: typeof MapPin
  value: number
  label: string
  testId: string
}

const StatCard = memo(function StatCard({
  stat,
  index,
}: {
  stat: StatItem
  index: number
}) {
  const Icon = stat.icon

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className="relative group"
      data-testid={stat.testId}
    >
      <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 p-4 sm:p-6 hover:bg-white/15 transition-all duration-300 group-hover:-translate-y-1 text-center">
        {/* Background gradient orb */}
        <div className="absolute -top-8 -right-8 w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white/10 blur-2xl group-hover:bg-white/20 transition-opacity" />

        {/* Icon */}
        <div className="relative mx-auto w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-white flex items-center justify-center mb-3 sm:mb-4 shadow-lg">
          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-900" />
        </div>

        {/* Value */}
        <div className="text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-1 sm:mb-2">{stat.value}</div>

        {/* Label */}
        <div className="text-xs sm:text-sm font-medium text-white/70">{stat.label}</div>
      </div>
    </motion.div>
  )
})

export function LocationsSection() {
  const { t } = useTranslation('home')

  const stats = useMemo(() => {
    const locationStats = getStats()
    return [
      {
        icon: MapPin,
        value: locationStats.italy,
        label: t('locations.stats.italy'),
        testId: 'italy-total',
      },
      {
        icon: Globe,
        value: locationStats.international,
        label: t('locations.stats.international'),
        testId: 'international-total',
      },
      {
        icon: Building2,
        value: locationStats.total,
        label: t('locations.stats.total'),
        testId: 'total-centers',
      },
    ]
  }, [t])

  return (
    <section
      id="locations-section"
      data-testid="locations-section"
      className="section-padding bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white/5 blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-white/5 blur-3xl" />

      <div className="container-custom relative">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-gray-900 border border-white/50 shadow-sm mb-4">
              <MapPin className="w-4 h-4" />
              <span className="text-sm font-medium">{t('locations.label')}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('locations.title')}
            </h2>
            <p className="text-lg text-white/70">{t('locations.subtitle')}</p>
          </motion.div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-8 sm:mb-10">
            {stats.map((stat, index) => (
              <StatCard key={stat.label} stat={stat} index={index} />
            ))}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="text-center"
          >
            <LocalizedLink
              to="locations"
              className="inline-flex items-center gap-2 px-6 py-3 sm:px-8 sm:py-4 bg-white text-gray-900 text-sm sm:text-base font-semibold rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl hover:bg-gray-100 transition-all duration-300 group"
            >
              <Globe className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>{t('locations.viewAll')}</span>
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:translate-x-1" />
            </LocalizedLink>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
